import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { getLastGoogleSignInUserCache } from '../../../utils/googleSignInCache';
import style from './GoogleButton.module.scss';

const getInitial = name => {
  if (!name) {
    return '?';
  }

  return (
    name
      .trim()
      .charAt(0)
      .toUpperCase() || '?'
  );
};

const normalizeAvatarUrl = avatarUrl => {
  if (!avatarUrl || typeof avatarUrl !== 'string') {
    return '';
  }

  return avatarUrl.trim();
};

const maskEmail = email => {
  if (!email || typeof email !== 'string') {
    return '';
  }

  const trimmedEmail = email.trim();
  const atIndex = trimmedEmail.indexOf('@');

  if (atIndex <= 0 || atIndex === trimmedEmail.length - 1) {
    return '';
  }

  const localPart = trimmedEmail.slice(0, atIndex);
  const domainPart = trimmedEmail.slice(atIndex + 1);
  const domainSegments = domainPart.split('.').filter(Boolean);

  if (!domainSegments.length) {
    return '';
  }

  const [domainName, ...domainTldParts] = domainSegments;
  const maskedLocal = `${localPart.charAt(0)}***`;
  const maskedDomain = domainName ? `${domainName.charAt(0)}***` : '***';
  const tldSuffix = domainTldParts.length ? `.${domainTldParts.join('.')}` : '';

  return `${maskedLocal}@${maskedDomain}${tldSuffix}`;
};

const GoogleButton = ({ label = 'Sign in with Google', onClick }) => {
  const lastGoogleUser = getLastGoogleSignInUserCache();
  const accountName = lastGoogleUser?.name || 'Google account';
  const maskedEmail = maskEmail(lastGoogleUser?.email);
  const avatarUrl = normalizeAvatarUrl(lastGoogleUser?.avatarUrl);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const hasAvatar = Boolean(avatarUrl) && !avatarLoadFailed;

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  return (
    <Link
      to="/auth/google"
      className={`${style.googleButton} ${lastGoogleUser ? style.googleButtonWithAccount : ''}`}
      onClick={onClick}
    >
      {lastGoogleUser && (
        <>
          <span className={style.accountAvatar}>
            {hasAvatar ? (
              <img
                src={avatarUrl}
                alt=""
                aria-hidden="true"
                className={style.accountAvatarImage}
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={() => {
                  setAvatarLoadFailed(true);
                }}
              />
            ) : (
              getInitial(lastGoogleUser.name || lastGoogleUser.email)
            )}
          </span>
          <span className={style.accountDetails}>
            <span className={style.accountName}>Sign in as {accountName}</span>
            {maskedEmail && (
              <span className={style.accountEmail}>{maskedEmail}</span>
            )}
          </span>
        </>
      )}
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        xmlns="http://www.w3.org/2000/svg"
        className={`${style.googleIcon} ${lastGoogleUser ? style.googleIconWithAccount : ''}`}
      >
        <g fill="none" fillRule="evenodd">
          <path
            d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
            fill="#4285F4"
          />
          <path
            d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
            fill="#34A853"
          />
          <path
            d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
            fill="#FBBC05"
          />
          <path
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
            fill="#EA4335"
          />
        </g>
      </svg>
      {lastGoogleUser && (
        <ChevronDown
          className={style.accountChevron}
          size={12}
          strokeWidth={2.25}
          aria-hidden="true"
        />
      )}
      {!lastGoogleUser && <span className={style.googleButtonText}>{label}</span>}
    </Link>
  );
};

export default GoogleButton;
